import { DiscountType, DiscountScope } from '@squareup/sq-addons-web-sdk';

import { OrderDiscountsConverter } from 'converters/order-discounts-converter';
import { buildPxCheck } from '../utils/buildPxCheck';
import { PaytronixApiError } from 'store/api/errors';
import { addDiscountNamePrefix } from 'utils/discount';
import {
  getLineItemAndModifierUidsForModifierDiscount,
  isDiscountUpdated,
} from 'store/api/discounts/utils/discountUtil';

const SIMULATE_ACCURAL_AND_REDEMPTION_API_ENDPOINT_PATH =
  '/check/simulateAccrualAndRedemption.json';

function formatExpirationDate(expirationDate) {
  const [year, month, day] = expirationDate.split('-'); // date is in yyyy-mm-dd format

  return `${month}/${day}/${year.substring(2)}`; // convert to mm/dd/yy format
}

function paytronixDiscountResponsesToDiscounts(discounts, scope, order) {
  return discounts.map((discount) => {
    const lineItemOrModifierUid = discount?.referenceLine;
    const lineItemAndModifierUidsIfModifierDiscount =
      getLineItemAndModifierUidsForModifierDiscount(
        lineItemOrModifierUid,
        order?.lineItems
      );

    const isModifierDiscount =
      lineItemAndModifierUidsIfModifierDiscount !== null;
    const lineItemUid = isModifierDiscount
      ? lineItemAndModifierUidsIfModifierDiscount.lineItemId
      : lineItemOrModifierUid;
    const modifierUid = isModifierDiscount
      ? lineItemAndModifierUidsIfModifierDiscount.modifierId
      : undefined;

    const metadata = {
      pxWalletCode: String(discount.walletCode),
      pxQuantityRedeemed: String(
        discount.quantityRedeemed / (discount.quantityDiscounted ?? 1)
      ),
      pxId: discount.id,
      pxDiscountType: discount.discountType,
      modifierUid,
    };

    const expirationDate = discount.soonestExpirationDate // yyyy-mm-dd format
      ? formatExpirationDate(discount.soonestExpirationDate)
      : undefined;

    if (expirationDate) {
      metadata.expirationDate = expirationDate;
    }

    return {
      name: addDiscountNamePrefix(discount.name),
      type: DiscountType.FIXED_AMOUNT,
      scope,
      amountMoney: {
        amount: Math.round((discount.unitDiscount ?? discount.discount) * 100),
        currency: 'USD',
      },
      lineItemId: lineItemUid,
      metadata,
    };
  });
}

function buildRequest(posContext, order, customer) {
  const check = buildPxCheck(order, customer?.referenceId);

  const cardInfo = {
    printedCardNumber: customer.referenceId,
    phoneNumber: customer.phoneNumber,
  };

  return {
    merchantId: Number(posContext.configurations.merchantId),
    storeCode: posContext.configurations.storeCode,
    check,
    cardInfo,
    autoActivateCard: true,
    terminal: posContext.device.id ?? '0',
    operator: posContext.employee.id ?? '0',
    rewardSelectionMode: {
      mode: 'applicable_rewards',
      chosenRewards: [],
    },
    potentialUnitItems: [],
    receiptSettings: [],
    potentialUnitItemDiscounts: [],
  };
}

function buildResponse(response, addedDiscounts, order) {
  // Filter out non-Paytronix discounts.
  const addedPaytronixDiscounts = addedDiscounts.filter(
    (discount) => !!discount.metadata?.pxId
  );
  const availableDiscounts = [
    paytronixDiscountResponsesToDiscounts(
      response.itemDiscounts,
      DiscountScope.LINE_ITEM,
      order
    ),
    paytronixDiscountResponsesToDiscounts(
      response.subtotalDiscounts,
      DiscountScope.ORDER
    ),
  ].flat();

  const appliedDiscounts = addedPaytronixDiscounts.filter(
    (addedPaytronixDiscount) => {
      const isDiscountNotAdded = response.rewardsToRemove.some(
        (rewardToRemove) => {
          return (
            Number(rewardToRemove.walletCode) ===
            Number(addedPaytronixDiscount.metadata.pxWalletCode)
          );
        }
      );

      return !isDiscountNotAdded;
    }
  );

  const discountsNotApplied = response.rewardsToRemove
    .map((rewardToRemove) => {
      const discount = addedPaytronixDiscounts.find(
        (addedPaytronixDiscount) =>
          Number(addedPaytronixDiscount.metadata?.pxWalletCode) ===
          Number(rewardToRemove.walletCode)
      );

      // TODO: There should always be a discount. We should log this to analytics
      // if this ever occurs.
      if (!discount) {
        window.console.error(
          'Could not find discount that has not been applied.'
        );

        return null;
      }

      return {
        discount,
        reason: rewardToRemove.reason,
      };
    })
    .filter((discountNotApplied) => !!discountNotApplied?.discount);

  const unavailableDiscounts = response.rewardBalances
    .filter((rewardBalance) => {
      const hasAvailableDiscount = availableDiscounts.some(
        (availableDiscount) =>
          Number(availableDiscount.metadata.pxWalletCode) ===
          rewardBalance.walletCode
      );
      const hasAppliedDiscount = appliedDiscounts.some(
        (appliedDiscount) =>
          Number(appliedDiscount.metadata.pxWalletCode) ===
          rewardBalance.walletCode
      );

      return !(hasAvailableDiscount || hasAppliedDiscount);
    })
    .map((rewardBalance) => rewardBalance.pointName);

  const updatedDiscounts = addedPaytronixDiscounts.filter((discount) =>
    isDiscountUpdated(discount)
  );

  return {
    result: response.result,
    customerName: response.customerName,
    availableDiscounts,
    appliedDiscounts,
    discountsNotApplied,
    unavailableDiscounts,
    updatedDiscounts,
    pointBalance: Number(response?.pointBalances[0]?.balance ?? 0),
  };
}

export async function simulateAccrualAndRedemption(
  posContext,
  pxContext,
  order,
  customer
) {
  const request = buildRequest(posContext, order, customer);
  const paytronixDomain = posContext.configurations.environmentUrl;

  const response = await fetch(
    `${paytronixDomain}${SIMULATE_ACCURAL_AND_REDEMPTION_API_ENDPOINT_PATH}`,
    {
      body: JSON.stringify(request),
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Basic ${pxContext.authorizationToken}`,
      },
    }
  ).then((result) => result.json());

  if (response.result === 'failure') {
    throw new PaytronixApiError({
      code: response.errorCode,
      message: response.errorMessage,
    });
  }

  return buildResponse(
    response,
    OrderDiscountsConverter.orderLineDiscountsToDiscounts(
      order.discounts ?? []
    ),
    order
  );
}
